import React from "react";
import styled from "@emotion/styled";

type SidebarProps = {
  open: boolean;
  offsetTop?: number;
  /**
   * When true, the sidebar fills its parent container's height (`height: 100%`)
   * and uses `position: relative` instead of `sticky`. Use this when the sidebar
   * is placed inside a flex container that already defines the available height,
   * so the sidebar reaches the bottom of that container instead of relying on
   * a viewport-based `calc(100vh - offsetTop)` height.
   *
   * @default false
   */
  fillContainer?: boolean;
  /** Open width in px. @default 350 */
  width?: number;
  children?: React.ReactNode;
};

/**
 * @deprecated Use the sidebar components from 'components/ui/sidebar/Sidebar' instead
 */
function Sidebar({
  open,
  offsetTop = 0,
  fillContainer = false,
  width = 350,
  children,
}: SidebarProps) {
  return (
    <SidebarWrapper open={open} offsetTop={offsetTop} fillContainer={fillContainer} width={width}>
      <SidebarContent>{children}</SidebarContent>
    </SidebarWrapper>
  );
}

const SidebarContent = styled.div({
  flex: 1,
  overflowY: "auto",
  minHeight: 0,
});

const SidebarWrapper = styled.div<{
  open: boolean;
  offsetTop: number;
  fillContainer: boolean;
  width: number;
}>(({ theme, open, offsetTop, fillContainer, width }) => ({
  position: fillContainer ? "relative" : "sticky",
  ...(fillContainer
    ? { height: "100%", maxHeight: "100%" }
    : {
        top: offsetTop,
        height: `calc(100vh - ${offsetTop}px)`,
        maxHeight: `calc(100vh - ${offsetTop}px)`,
      }),
  width: open ? `${width}px` : "0px",
  minWidth: 0,
  backgroundColor: theme.colors.sysBackgroundDefault,
  overflow: "hidden",
  transition: "width 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  display: "flex",
  flexDirection: "column",
  borderRight: open ? `1px solid ${theme.colors.sysBorderDecorative}` : 0,
  borderBottom: open ? `1px solid ${theme.colors.sysBorderDecorative}` : 0,
}));

export default Sidebar;
